// src/components/InviteUserModal.jsx
import React, { useMemo, useState } from 'react';
import { inviteUser as inviteUserApi } from '../services/userService';
import { toast } from 'react-toastify';

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function InviteUserModal({ isOpen, onClose, onSuccess }) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    role: 'staff',
  });
  const [loading, setLoading] = useState(false);
  const [resend, setResend] = useState(false); // harmless: backend tolerates it

  const emailValid = useMemo(() => emailRe.test(String(form.email).trim()), [form.email]);
  const isFormValid = emailValid && !loading; // name is optional in backend

  const resetAndClose = () => {
    setForm({ name: '', email: '', role: 'staff' });
    setResend(false);
    onClose?.();
  };

  const onInvite = async () => {
    if (!isFormValid) return;
    setLoading(true);
    try {
      const payload = {
        name: String(form.name || '').trim(),
        email: String(form.email || '').trim(),
        role: form.role,
        resend,
      };
      const res = await inviteUserApi(payload);
      const invitedEmail = res?.user?.email || payload.email;

      // Success UX
      toast.success(res?.message || `Invitation sent to ${invitedEmail}.`);
      onSuccess?.(res?.user);
      resetAndClose();
    } catch (err) {
      if (err?._is429) {
        const after = err?._retryAfter ? ` Try again in ~${err._retryAfter}s.` : '';
        toast.warning(`Invite rate limit reached.${after}`);
      } else if (err?._status === 409) {
        // user_exists conflict from backend
        toast.error('A user with this email already exists.');
      } else if (err?._is422) {
        toast.error(err?._data?.message || 'Please check the details and try again.');
      } else {
        const apiMsg = err?._data?.message || err?.response?.data?.message || err?.message;
        toast.error(apiMsg || 'Failed to send invite.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) resetAndClose();
      }}
    >
      <div className="bg-white w-full max-w-md rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Invite New User</h2>
          <button
            type="button"
            onClick={resetAndClose}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">Name (optional)</label>
            <input
              className="mt-1 w-full border rounded px-3 py-2 border-gray-300 focus:border-gray-500 outline-none"
              placeholder="Jane Doe"
              value={form.name}
              onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input
              className={`mt-1 w-full border rounded px-3 py-2 outline-none ${
                form.email && !emailValid ? 'border-red-300' : 'border-gray-300 focus:border-gray-500'
              }`}
              placeholder="user@company.com"
              type="email"
              value={form.email}
              onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
              required
            />
            {form.email && !emailValid && (
              <p className="mt-1 text-xs text-red-600">Enter a valid email address.</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Role</label>
            <select
              className="mt-1 w-full border rounded px-3 py-2 border-gray-300 focus:border-gray-500 outline-none"
              value={form.role}
              onChange={(e) => setForm((s) => ({ ...s, role: e.target.value }))}
            >
              <option value="staff">Staff</option>
              <option value="viewer">Viewer</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700 select-none">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={resend}
              onChange={(e) => setResend(e.target.checked)}
            />
            Resend if already invited
          </label>
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={resetAndClose}
            className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onInvite}
            disabled={!isFormValid}
            className="px-4 py-2 rounded bg-black text-white disabled:opacity-60"
          >
            {loading ? 'Sending…' : 'Send invite'}
          </button>
        </div>

        <p className="mt-3 text-xs text-gray-500">
          The user will receive an email with a link to set their password and activate their account.
        </p>
      </div>
    </div>
  );
}
